import {KEY_CODES} from 'definitions/constant'
import {formatDistanceToNow, subDays} from 'date-fns'
import {formatInTimeZone} from 'date-fns-tz'

import {DopamineSessionSummary} from 'interfaces'
import {getBrowserTimeZone} from 'utils'

declare const dopamineAssistantStorageScopeBrand: unique symbol

const DOPAMINE_ASSISTANT_COLLAPSE_EVENT = 'dopamine-assistant:collapse'

export const requestDopamineAssistantCollapse = (): void => {
  window.dispatchEvent(new Event(DOPAMINE_ASSISTANT_COLLAPSE_EVENT))
}

export const subscribeToDopamineAssistantCollapse = (
  listener: () => void,
): (() => void) => {
  window.addEventListener(DOPAMINE_ASSISTANT_COLLAPSE_EVENT, listener)
  return () =>
    window.removeEventListener(DOPAMINE_ASSISTANT_COLLAPSE_EVENT, listener)
}

export type DopamineAssistantStorageScope = string & {
  readonly [dopamineAssistantStorageScopeBrand]: true
}

// Resizable widths (past chats sidebar, open panel): limits, clamping and a
// remembered value per account.
export type DopamineWidthLimits = {max: number; min: number}

export const clampDopamineWidth = (
  width: number,
  {max, min}: DopamineWidthLimits,
): number => Math.round(Math.min(Math.max(min, width), max))

export const readDopamineStoredWidth = (
  storageKey: string,
  scope: DopamineAssistantStorageScope | null,
): number | null => {
  if (!scope) return null

  try {
    const value = window.sessionStorage.getItem(`${storageKey}:${scope}`)
    const width = value === null ? Number.NaN : Number(value)
    return Number.isFinite(width) && width > 0 ? width : null
  } catch {
    return null
  }
}

export const writeDopamineStoredWidth = (
  storageKey: string,
  scope: DopamineAssistantStorageScope | null,
  width: number | null,
): void => {
  if (!scope) return

  try {
    const key = `${storageKey}:${scope}`
    if (width === null) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, String(width))
  } catch {
    // Storage unavailable: the width simply goes back to its default next time.
  }
}

// Below this viewport width past chats stay a popover in full screen; the sidebar needs room beside the reading column.
export const DOPAMINE_HISTORY_SIDEBAR_MIN_VIEWPORT_WIDTH = 1280

// Past chats sidebar. The default width is the Tailwind token
// `w-dopamine-history-sidebar` (clamp(17rem, 17.5vw, 28rem)); these mirror it
// for the separator's accessible value and keyboard nudges.
const DOPAMINE_HISTORY_SIDEBAR_MIN_WIDTH_PX = 240
const DOPAMINE_HISTORY_SIDEBAR_MAX_WIDTH_PX = 480
const DOPAMINE_HISTORY_SIDEBAR_DEFAULT_MIN_PX = 272
const DOPAMINE_HISTORY_SIDEBAR_DEFAULT_MAX_PX = 448
const DOPAMINE_HISTORY_SIDEBAR_DEFAULT_VIEWPORT_SHARE = 0.175
const DOPAMINE_OPEN_STATE_STORAGE_KEY = 'dopamine-assistant.open'

export const DOPAMINE_HISTORY_SIDEBAR_WIDTH_STORAGE_KEY =
  'dopamine-assistant.history-sidebar-width'

export const getDopamineHistorySidebarWidthLimits =
  (): DopamineWidthLimits => ({
    max: DOPAMINE_HISTORY_SIDEBAR_MAX_WIDTH_PX,
    min: DOPAMINE_HISTORY_SIDEBAR_MIN_WIDTH_PX,
  })

export const clampDopamineHistorySidebarWidth = (width: number): number =>
  clampDopamineWidth(width, getDopamineHistorySidebarWidthLimits())

export const getDopamineHistorySidebarDefaultWidth = (
  viewportWidth: number,
): number =>
  Math.round(
    Math.min(
      Math.max(
        DOPAMINE_HISTORY_SIDEBAR_DEFAULT_MIN_PX,
        viewportWidth * DOPAMINE_HISTORY_SIDEBAR_DEFAULT_VIEWPORT_SHARE,
      ),
      DOPAMINE_HISTORY_SIDEBAR_DEFAULT_MAX_PX,
    ),
  )

export const readDopamineHistorySidebarWidth = (
  scope: DopamineAssistantStorageScope | null,
): number | null =>
  readDopamineStoredWidth(DOPAMINE_HISTORY_SIDEBAR_WIDTH_STORAGE_KEY, scope)

export const writeDopamineHistorySidebarWidth = (
  scope: DopamineAssistantStorageScope | null,
  width: number | null,
): void =>
  writeDopamineStoredWidth(
    DOPAMINE_HISTORY_SIDEBAR_WIDTH_STORAGE_KEY,
    scope,
    width,
  )

// The open panel. Resizable only where it is not already viewport-wide (the
// 64rem media query in DopamineAssistant.css). The default mirrors the Tailwind
// token `w-dopamine-assistant` = min(clamp(23.75rem, 23vw, 30rem), 100vw).
// Below 1280 the panel already takes most of the row, so there is nothing to
// give; the width stays fixed as before.
export const DOPAMINE_PANEL_RESIZE_MIN_VIEWPORT_WIDTH = 1280
// 360–500 px: enough to read a table, never so wide that the page beside it
// stops being the page. At 1280 the content still keeps 780 px.
const DOPAMINE_PANEL_MIN_WIDTH_PX = 360
const DOPAMINE_PANEL_MAX_WIDTH_PX = 500
// A drag that ends this close to the default lands exactly on it.
export const DOPAMINE_PANEL_SNAP_TO_DEFAULT_PX = 8
const DOPAMINE_PANEL_DEFAULT_MIN_PX = 380
const DOPAMINE_PANEL_DEFAULT_MAX_PX = 480
const DOPAMINE_PANEL_DEFAULT_VIEWPORT_SHARE = 0.23
export const DOPAMINE_PANEL_WIDTH_STORAGE_KEY = 'dopamine-assistant.panel-width'

export const getDopaminePanelWidthLimits = (): DopamineWidthLimits => ({
  max: DOPAMINE_PANEL_MAX_WIDTH_PX,
  min: DOPAMINE_PANEL_MIN_WIDTH_PX,
})

export const clampDopaminePanelWidth = (width: number): number =>
  clampDopamineWidth(width, getDopaminePanelWidthLimits())

/**
 * The open panel's width on screen, for layout decisions made outside the
 * shell. Zero when the panel is collapsed or not rendered.
 */
export const getDopamineAssistantPanelWidth = (): number => {
  if (typeof document === 'undefined') return 0

  const panel = document.querySelector('[data-cy="dopamine-assistant-panel"]')
  return panel instanceof HTMLElement ? panel.getBoundingClientRect().width : 0
}

export const getDopaminePanelDefaultWidth = (viewportWidth: number): number =>
  Math.round(
    Math.min(
      Math.max(
        DOPAMINE_PANEL_DEFAULT_MIN_PX,
        viewportWidth * DOPAMINE_PANEL_DEFAULT_VIEWPORT_SHARE,
      ),
      DOPAMINE_PANEL_DEFAULT_MAX_PX,
      viewportWidth,
    ),
  )

export const readDopaminePanelWidth = (
  scope: DopamineAssistantStorageScope | null,
): number | null =>
  readDopamineStoredWidth(DOPAMINE_PANEL_WIDTH_STORAGE_KEY, scope)

export const writeDopaminePanelWidth = (
  scope: DopamineAssistantStorageScope | null,
  width: number | null,
): void =>
  writeDopamineStoredWidth(DOPAMINE_PANEL_WIDTH_STORAGE_KEY, scope, width)

// Whether the panel was open, so leaving Analytics and coming back (or a
// reload) restores it. Full screen is deliberately not remembered.
export const readDopamineAssistantOpenState = (
  scope: DopamineAssistantStorageScope | null,
): boolean => {
  if (!scope) return false

  try {
    return (
      window.sessionStorage.getItem(
        `${DOPAMINE_OPEN_STATE_STORAGE_KEY}:${scope}`,
      ) === 'true'
    )
  } catch {
    return false
  }
}

export const writeDopamineAssistantOpenState = (
  scope: DopamineAssistantStorageScope | null,
  isOpen: boolean,
): void => {
  if (!scope) return

  try {
    window.sessionStorage.setItem(
      `${DOPAMINE_OPEN_STATE_STORAGE_KEY}:${scope}`,
      String(isOpen),
    )
  } catch {
    // Storage unavailable: the panel simply starts closed next time.
  }
}

// The toggle shortcut as users see it on their platform.
export const getDopamineShortcutLabel = (): string =>
  /Mac|iPhone|iPad/i.test(window.navigator.platform) ? '⌘/' : 'Ctrl+/'

export const isDopamineShortcut = (event: {
  altKey: boolean
  shiftKey: boolean
  ctrlKey: boolean
  key: string
  metaKey: boolean
}): boolean =>
  !event.altKey &&
  !event.shiftKey &&
  (event.metaKey || event.ctrlKey) &&
  event.key === KEY_CODES.SLASH

export const createDopamineAssistantStorageScope = (
  account: string,
  tenant: string,
): DopamineAssistantStorageScope | null => {
  const normalizedAccount = account.trim().toLowerCase()
  const normalizedTenant = tenant.trim().toLowerCase()

  if (!normalizedAccount || !normalizedTenant) return null

  return JSON.stringify([
    normalizedAccount,
    normalizedTenant,
  ]) as DopamineAssistantStorageScope
}

export const createDopamineAssistantSessionId = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()

  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
}

export const doesDopamineSessionMatchSearch = (
  session: DopamineSessionSummary,
  query: string,
): boolean => session.title.toLowerCase().includes(query.toLowerCase())

export type DopamineSessionDayGroup = 'today' | 'yesterday' | 'earlier'

export type DopamineSessionDayGroups = {
  group: DopamineSessionDayGroup
  sessions: DopamineSessionSummary[]
}[]

const DAY_GROUP_ORDER: readonly DopamineSessionDayGroup[] = [
  'today',
  'yesterday',
  'earlier',
]

const toLocalDay = (date: Date, timeZone: string): string =>
  formatInTimeZone(date, timeZone, 'yyyy-MM-dd')

export const getDopamineSessionDayGroup = (
  date: string,
  now: Date = new Date(),
): DopamineSessionDayGroup => {
  const timeZone = getBrowserTimeZone()
  const day = toLocalDay(new Date(date), timeZone)

  if (day === toLocalDay(now, timeZone)) return 'today'
  if (day === toLocalDay(subDays(now, 1), timeZone)) return 'yesterday'
  return 'earlier'
}

// Under a day heading the time can be short: relative today, clock time
// yesterday, and date plus time for anything earlier.
export const formatDopamineSessionTime = (
  date: string,
  group: DopamineSessionDayGroup,
): string => {
  if (!date) return ''

  const timeZone = getBrowserTimeZone()
  const value = new Date(date)

  if (group === 'today') return formatDistanceToNow(value, {addSuffix: true})
  if (group === 'yesterday') return formatInTimeZone(value, timeZone, 'hh:mm a')
  return `${formatInTimeZone(
    value,
    timeZone,
    'dd MMM yyyy',
  )} · ${formatInTimeZone(value, timeZone, 'hh:mm a')}`
}

export const groupDopamineSessionsByDay = (
  sessions: DopamineSessionSummary[],
  now: Date = new Date(),
): DopamineSessionDayGroups => {
  const buckets: Record<DopamineSessionDayGroup, DopamineSessionSummary[]> = {
    earlier: [],
    today: [],
    yesterday: [],
  }
  sessions.forEach(session => {
    buckets[getDopamineSessionDayGroup(session.createdAt, now)].push(session)
  })

  return DAY_GROUP_ORDER.filter(group => buckets[group].length > 0).map(
    group => ({group, sessions: buckets[group]}),
  )
}

const MARKDOWN_LIST_ITEM_PATTERN = /^(?:[-*]|\d+\.)\s+(\S.*)$/
const NEXT_STEPS_HEADING_PATTERN = /^\s*(?:\*\*|__)?next\b:?/i
// Paired markers only, so identifiers such as svc_account keep their
// underscores; single * and _ count only at word edges.
const MARKDOWN_STRONG_OR_CODE_PATTERN = /(\*\*|__|`)(.+?)\1/g
const MARKDOWN_EMPHASIS_PATTERN = /(^|\W)([*_])(.+?)\2(?!\w)/g

// Move past each inspected label once, including malformed links.
const stripMarkdownLinks = (text: string): string => {
  const parts: string[] = []
  let cursor = 0
  let searchFrom = 0
  while (searchFrom < text.length) {
    const open = text.indexOf('[', searchFrom)
    if (open === -1) break
    const close = text.indexOf(']', open + 1)
    if (close === -1) break
    searchFrom = close + 1
    if (close === open + 1 || text[close + 1] !== '(') continue
    const end = text.indexOf(')', close + 2)
    if (end === -1) break
    parts.push(text.slice(cursor, open), text.slice(open + 1, close))
    cursor = end + 1
    searchFrom = cursor
  }
  parts.push(text.slice(cursor))
  return parts.join('')
}

const stripInlineMarkdown = (text: string): string =>
  stripMarkdownLinks(text)
    .replace(MARKDOWN_STRONG_OR_CODE_PATTERN, '$2')
    .replace(MARKDOWN_EMPHASIS_PATTERN, '$1$3')
    .trim()

// The first list items after an answer's "Next:" paragraph, as plain text,
// so they can be offered back as one-click follow-up questions.
export const getDopamineFollowUps = (markdown: string, limit = 2): string[] => {
  const lines = markdown.split('\n')
  const headingIndex = lines.findIndex(line =>
    NEXT_STEPS_HEADING_PATTERN.test(line),
  )
  if (headingIndex === -1) return []

  const followUps: string[] = []
  for (const line of lines.slice(headingIndex + 1)) {
    const item = MARKDOWN_LIST_ITEM_PATTERN.exec(line.trim())
    if (item) {
      followUps.push(stripInlineMarkdown(item[1]))
      if (followUps.length === limit) break
      continue
    }
    if (followUps.length > 0 && line.trim() !== '') break
  }

  return followUps.filter(Boolean)
}
